import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Copy,
  Info,
  Lock,
  MoreVertical,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
} from "lucide-react";
import { DsBadge, DsButton, DsIcon, DsInput } from "../../../design-system";
import { filterCatalogByCompanyModules } from "../../../utils/companyModules";
import { getScreenCatalogForRole } from "../../../rbac/catalog";
import { isDefaultSystemRole, isModuleGranted, isSystemRole, normalizeRoleWizardType } from "./roleAccessUtils";
import { modulesGrantedCount, roleRisks } from "./roleRisks";

/**
 * Roles visible on the page: custom roles always; Employee / Self and Reporting Manager
 * only when "Show default roles" is on; any other system role (Admin…) never.
 */
export function visibleRolesFor(roles, { search, showDefaultRoles }) {
  const q = String(search || "").trim().toLowerCase();
  return (Array.isArray(roles) ? roles : [])
    .filter((r) => (isDefaultSystemRole(r) ? showDefaultRoles : !isSystemRole(r)))
    .filter(
      (r) =>
        !q ||
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.code || "").toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const d = (isDefaultSystemRole(a) ? 0 : 1) - (isDefaultSystemRole(b) ? 0 : 1);
      return d || String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
    });
}

function MenuItem({ icon, label, onClick, disabled, title, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={title || undefined}
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-45 ${
        danger
          ? "text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.08)]"
          : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
      }`}
    >
      <DsIcon icon={icon} size="sm" />
      {label}
    </button>
  );
}

function ModuleMeter({ catalog, access }) {
  return (
    <div className="flex gap-0.5" aria-hidden>
      {(catalog || []).map((m) => (
        <i
          key={m.key}
          className={`h-1.5 flex-1 rounded-sm ${
            isModuleGranted(m, access?.[m.key])
              ? "bg-[hsl(var(--ds-primary))]"
              : "bg-[hsl(var(--ds-border))]"
          }`}
        />
      ))}
    </div>
  );
}

export default function RoleListView({
  roles,
  visibleRoles,
  search,
  setSearch,
  showDefaultRoles,
  setShowDefaultRoles,
  selectedRoleId,
  onSelectRole,
  isDelegate,
  getBlockers,
  onRoleAction,
  accessByRole,
  onNewRole,
  canCreateRole,
  layout = "cards",
}) {
  const defaultCount = (Array.isArray(roles) ? roles : []).filter(isDefaultSystemRole).length;
  const [menu, setMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setMenu(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu]);

  const openMenu = (e, role) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const width = 220;
    const left = Math.min(rect.right - width, window.innerWidth - width - 8);
    const top = Math.min(rect.bottom + 4, window.innerHeight - 220);
    setMenu({ role, top: Math.max(8, top), left: Math.max(8, left) });
  };

  const run = (kind) => {
    if (!menu?.role) return;
    onRoleAction?.(kind, menu.role);
    setMenu(null);
  };

  const menuRole = menu?.role;
  const menuSystem = isSystemRole(menuRole);
  const menuUsesAccess = menuRole ? menuSystem || normalizeRoleWizardType(menuRole) === "access_based" : false;
  const blockers = menuRole && getBlockers ? getBlockers(menuRole) : {};

  const toolbar = (
    <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[hsl(var(--ds-border))] p-3">
      <div className="relative min-w-[180px] flex-1">
        <DsIcon
          icon={Search}
          size="sm"
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[hsl(var(--ds-muted-fg))]"
        />
        <DsInput
          type="search"
          placeholder="Search roles"
          aria-label="Search roles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
        />
      </div>
      {!isDelegate && defaultCount > 0 ? (
        <label className="flex cursor-pointer items-center gap-2 text-xs text-[hsl(var(--ds-fg))]">
          <input
            type="checkbox"
            role="switch"
            aria-checked={showDefaultRoles}
            checked={showDefaultRoles}
            onChange={(e) => setShowDefaultRoles(e.target.checked)}
            className="h-4 w-4 accent-[hsl(var(--ds-primary))]"
          />
          Show default roles
        </label>
      ) : null}
    </div>
  );

  const menuPortal =
    menu && menuRole
      ? createPortal(
          <>
            <div className="fixed inset-0 z-[12360]" aria-hidden onClick={() => setMenu(null)} />
            <div
              ref={menuRef}
              className="fixed z-[12361] w-[220px] overflow-hidden rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-[var(--ds-shadow-card)]"
              style={{ top: menu.top, left: menu.left }}
              role="menu"
              aria-label={`Actions for ${menuRole.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem icon={Info} label="Info" onClick={() => run("info")} />
              {menuUsesAccess && !menuSystem ? (
                <MenuItem
                  icon={Pencil}
                  label="Edit role"
                  disabled={!!blockers.edit}
                  title={blockers.edit || undefined}
                  onClick={() => run("edit")}
                />
              ) : null}
              {menuUsesAccess && !menuSystem ? (
                <MenuItem
                  icon={Copy}
                  label="Copy role permissions"
                  disabled={!!blockers.copy}
                  title={blockers.copy || undefined}
                  onClick={() => run("copy")}
                />
              ) : null}
              {!menuSystem ? (
                <MenuItem
                  icon={Power}
                  label={menuRole.active ? "Deactivate" : "Reactivate"}
                  disabled={!!blockers.toggle}
                  title={blockers.toggle || undefined}
                  onClick={() => run("toggle")}
                />
              ) : null}
              {!menuSystem && menuRole.deletable !== false ? (
                <MenuItem
                  icon={Trash2}
                  label="Delete role"
                  danger
                  disabled={!!blockers.delete}
                  title={blockers.delete || undefined}
                  onClick={() => run("delete")}
                />
              ) : null}
            </div>
          </>,
          document.body
        )
      : null;

  if (layout === "cards") {
    return (
      <section
        aria-label="Roles"
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
      >
        {toolbar}
        <div className="scroll-area min-h-0 flex-1 overflow-y-auto p-4">
          {visibleRoles.length === 0 ? (
            <div className="flex flex-1 items-center justify-center p-6 text-center">
              <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
                {search ? "No roles match your search." : "No custom roles yet. Use New role to add one."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {visibleRoles.map((role) => {
                const selected = String(role.id) === String(selectedRoleId);
                const system = isSystemRole(role);
                const catalog = filterCatalogByCompanyModules(getScreenCatalogForRole(role));
                const access = accessByRole?.[role.id];
                const risks = roleRisks(role, catalog, access);
                const granted = modulesGrantedCount(catalog, access);
                return (
                  <div
                    key={role.id}
                    className={`relative flex flex-col gap-2.5 rounded-[11px] border p-4 text-left transition-colors ${
                      selected
                        ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary)/0.08)]"
                        : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] hover:border-[hsl(var(--ds-primary)/0.55)]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onSelectRole(role.id);
                        onRoleAction?.("openPermissions", role);
                      }}
                      className="flex flex-col gap-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                    >
                      <div className="flex items-start justify-between gap-2 pr-8">
                        <h3 className="text-[17px] font-bold leading-snug text-[hsl(var(--ds-fg))]">{role.name}</h3>
                        {system ? (
                          <DsBadge tone="neutral">System</DsBadge>
                        ) : risks.length ? (
                          <DsBadge tone="danger">Risk</DsBadge>
                        ) : (
                          <DsBadge tone="primary">Custom</DsBadge>
                        )}
                      </div>
                      {role.description ? (
                        <p className="line-clamp-2 text-sm text-[hsl(var(--ds-muted-fg))]">{role.description}</p>
                      ) : (
                        <p className="text-sm text-[hsl(var(--ds-muted-fg))]">
                          {system ? "System role" : "Custom role"}
                          {role.isOwnRole ? " · Your role" : ""}
                        </p>
                      )}
                      <ModuleMeter catalog={catalog} access={access} />
                      <div className="flex justify-between text-xs text-[hsl(var(--ds-muted-fg))]">
                        <span>{role.assignedUserCount ?? 0} users</span>
                        <span>
                          {granted} of {catalog.length} modules
                        </span>
                      </div>
                      {role.isOwnRole ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                          <DsIcon icon={Lock} size="sm" /> Your role
                        </span>
                      ) : null}
                      {!role.active ? <DsBadge tone="warning">Inactive</DsBadge> : null}
                    </button>
                    <button
                      type="button"
                      aria-label={`Actions for ${role.name}`}
                      aria-haspopup="menu"
                      aria-expanded={menuRole && String(menuRole.id) === String(role.id)}
                      onClick={(e) => openMenu(e, role)}
                      className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                    >
                      <DsIcon icon={MoreVertical} size="sm" />
                    </button>
                  </div>
                );
              })}
              <button
                type="button"
                disabled={!canCreateRole}
                onClick={() => onNewRole?.()}
                className="flex min-h-[140px] flex-col items-center justify-center gap-2 rounded-[11px] border border-dashed border-[hsl(var(--ds-primary)/0.55)] p-4 text-sm font-semibold text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.06)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-45"
              >
                <DsIcon icon={Plus} size="sm" />
                New role
              </button>
            </div>
          )}
        </div>
        {menuPortal}
      </section>
    );
  }

  // Compact list used when selecting a role inside Permissions tab
  return (
    <section
      aria-label="Roles"
      className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
    >
      {toolbar}
      {visibleRoles.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-6 text-center">
          <p className="ds-body text-[hsl(var(--ds-muted-fg))]">
            {search ? "No roles match your search." : "No custom roles yet."}
          </p>
        </div>
      ) : (
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto p-2" role="listbox" aria-label="Role list">
          {visibleRoles.map((role) => {
            const selected = String(role.id) === String(selectedRoleId);
            return (
              <li key={role.id} role="option" aria-selected={selected}>
                <button
                  type="button"
                  onClick={() => onSelectRole(role.id)}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] ${
                    selected
                      ? "bg-[hsl(var(--ds-primary)/0.08)] text-[hsl(var(--ds-primary))]"
                      : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                  }`}
                >
                  <span className="min-w-0 flex-1 truncate">{role.name}</span>
                  {isSystemRole(role) ? <DsBadge tone="neutral">System</DsBadge> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {canCreateRole ? (
        <div className="shrink-0 border-t border-[hsl(var(--ds-border))] p-2">
          <DsButton size="sm" variant="outline" className="w-full" onClick={() => onNewRole?.()}>
            <DsIcon icon={Plus} size="sm" />
            New role
          </DsButton>
        </div>
      ) : null}
      {menuPortal}
    </section>
  );
}
